/**
 * D.2.b — Corpus Search Window (Win95 chrome on top of Azure AI Search).
 *
 * Flow:
 *   1. SPA reads `clientSlug` from the portal context.
 *   2. Calls `api.searchToken.issueForPortal({clientSlug})` → returns
 *      configured config (or {configured:false} if the env vars aren't
 *      set on Convex yet).
 *   3. User picks a search mode (Keyword / Vector / Hybrid) and types
 *      a query.
 *   4. For Vector / Hybrid, SPA calls `api.searchToken.embedQuery` to
 *      get a 1536-dim vector server-side (OpenAI key never reaches the
 *      browser). Then POSTs to Azure AI Search with the filterClause +
 *      mode body shape.
 *   5. Results render as a list with title + snippet + source pill +
 *      score (or reranker score when semantic is on).
 *
 * Phase E additions:
 *   - Mode toggle (keyword | vector | hybrid)
 *   - Semantic ranker checkbox (only meaningful when Azure semantic is
 *     provisioned on the index; falls back gracefully otherwise)
 *
 * When invoked from the bare `/` desktop (no portal), the window renders
 * a "open a portal to search its corpus" empty state.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { useAction, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useOptionalTenantBranding } from '../../lib/tenant/TenantBrandingContext';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { useRepancyRole } from '../../lib/auth/useRepancyRole';
import {
  searchCorpus,
  type CorpusHit,
  type SearchMode,
} from '../../lib/corpus/azureSearchClient';
import { testIds } from '../../lib/testIds';

interface CorpusSearchWindowProps {
  /** Optional fetch override for tests. */
  fetchImpl?: typeof fetch;
  /** Optional searchCorpus override; defaults to the real REST client. */
  searchImpl?: typeof searchCorpus;
}

/**
 * XP Start Menu Search → Corpus Search bridge.
 *
 * The Start Menu dispatches `repancy:start-menu-search` with the
 * typed query as `detail.query`. CorpusSearchWindow listens once it
 * mounts (Win95Desktop opens it from the menu) and seeds its input.
 */
export const START_MENU_SEARCH_EVENT = 'repancy:start-menu-search';
export interface StartMenuSearchDetail {
  query: string;
}

/**
 * F-AZURE-SEARCH Phase 3 — plain-language mode labels.
 *
 * Users don't think in vector-search terms. Each mode gets a friendly
 * label + a one-sentence tooltip explaining when to use it. Order:
 * Keyword → Meaning → Both. Default is "Both" (hybrid) — best out-of-
 * the-box experience per D-AS-4.
 */
const MODES: ReadonlyArray<{
  id: SearchMode;
  label: string;
  icon: string;
  tooltip: string;
}> = [
  {
    id: 'keyword',
    label: 'Keyword',
    icon: '🔤',
    tooltip:
      'Match the exact words I type. Best when I remember a specific phrase or filename.',
  },
  {
    id: 'vector',
    label: 'Meaning',
    icon: '🧠',
    tooltip:
      'Find docs about the same topic, even if they use different words. Searching "non-compete clauses" finds docs that say "restrictive covenants."',
  },
  {
    id: 'hybrid',
    label: 'Both',
    icon: '⚖',
    tooltip:
      'Best of both — runs Keyword and Meaning together and combines the results. Recommended.',
  },
];

/**
 * B.2.b — small visual affordance so the demo storytelling makes the
 * admin-vs-non-admin contrast obvious. Renders nothing when the user
 * has no recognised role (anonymous evaluator or legacy `repancy_admin`).
 */
function RoleIndicator() {
  const { role, isLoading } = useRepancyRole();
  if (isLoading) return null;
  if (role === null) return null;
  const label =
    role === 'operator' ? 'Admin' : role === 'associate' ? 'Associate' : 'Admin';
  const tone =
    role === 'operator'
      ? 'border-amber-700 bg-amber-100 text-amber-900'
      : 'border-blue-700 bg-blue-100 text-blue-900';
  return (
    <span
      data-testid={testIds.auth.roleIndicator}
      data-role={role}
      className={`shrink-0 border px-2 py-0.5 text-xs font-semibold ${tone}`}
      title={`Role: ${role}`}
    >
      {label}
    </span>
  );
}

export function CorpusSearchWindow({
  fetchImpl,
  searchImpl,
}: CorpusSearchWindowProps = {}): ReactElement {
  // 2026-05-28 — useResolvedPortalSlug pulls from the React
  // context first, then falls back to /c/<slug>/... in the URL.
  // Without the fallback, this window goes silent when rendered
  // by the OUTER WindowLayer (outside TenantBrandingProvider).
  //
  // We keep `portal` (the full branding context) for the
  // displayName placeholder label — that's used only when the
  // context IS in scope; non-portal users see the slug fallback.
  const clientSlug = useResolvedPortalSlug();
  const portal = useOptionalTenantBranding();

  const tokenConfig = useQuery(
    api.searchToken.issueForPortal,
    clientSlug !== null ? { clientSlug } : 'skip',
  );

  // F-AZURE-SEARCH Phase 3 — replace hardcoded slug links in the
  // empty-portal state with the user's real portal memberships.
  // Returns `null` when unauthenticated; falls back to the hardcoded
  // links for operators inspecting from outside a portal.
  const myPortals = useQuery(
    api.portalAccess.listMyPortals,
    clientSlug === null ? {} : 'skip',
  );

  // Convex action: server-side embedding via Azure OpenAI.
  // Pulled out so the SPA never holds the OpenAI key.
  const embedQuery = useAction(api.searchToken.embedQuery);

  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  // F-AZURE-SEARCH Phase 3 — default to Hybrid ("Both") per D-AS-4.
  // Hybrid is the best-out-of-the-box experience; users can downshift
  // to Keyword if they want exact-word match.
  const [mode, setMode] = useState<SearchMode>('hybrid');
  // Semantic reranker stays opt-in — D-AS-4.
  const [semantic, setSemantic] = useState(false);
  // Inline help disclosure ("How does this work?") collapsed by default.
  const [helpOpen, setHelpOpen] = useState(false);
  const [hits, setHits] = useState<CorpusHit[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // F-NIPE-DESKTOP-AWARENESS Phase 2 — read-only bridge for
  // readWindowContext('/corpus-search'). NIPE reads what the user is
  // searching for + how many hits they're looking at so it can pivot
  // to a follow-up (e.g. "open the top hit in Document Library").
  const corpusBridgeRef = useRef({
    query,
    submittedQuery,
    mode,
    helpOpen,
    hits,
    loading,
    error,
  });
  corpusBridgeRef.current = {
    query,
    submittedQuery,
    mode,
    helpOpen,
    hits,
    loading,
    error,
  };
  useEffect(() => {
    window.__repancyCorpusSearch = () => {
      const cur = corpusBridgeRef.current;
      return {
        query: cur.query,
        submittedQuery: cur.submittedQuery,
        mode: String(cur.mode),
        helpOpen: cur.helpOpen,
        hitCount: cur.hits === null ? null : cur.hits.length,
        loading: cur.loading,
        errorMessage: cur.error,
      };
    };
    return () => {
      delete window.__repancyCorpusSearch;
    };
  }, []);

  const doSearch = useCallback(
    async (q: string, m: SearchMode, sem: boolean) => {
      if (!tokenConfig || tokenConfig.configured !== true) {return;}
      if (q.trim().length === 0) {return;}
      const run = searchImpl ?? searchCorpus;
      setLoading(true);
      setError(null);
      try {
        let vector: number[] | undefined;
        if (m === 'vector' || m === 'hybrid') {
          const embedded = await embedQuery({ query: q });
          if (!embedded.configured) {
            throw new Error(
              'Vector search is not configured on this deployment. ' +
                'Ask an operator to set AZURE_OPENAI_* env vars on Convex.',
            );
          }
          vector = embedded.vector;
        }
        const r = await run({
          endpoint: tokenConfig.searchEndpoint,
          indexName: tokenConfig.indexName,
          queryKey: tokenConfig.searchKey,
          query: q,
          filterClause: tokenConfig.filterClause,
          mode: m,
          ...(vector !== undefined ? { vector } : {}),
          semantic: sem,
          fetchImpl,
        });
        setHits(r.hits);
        setTotal(r.totalCount);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setHits(null);
      } finally {
        setLoading(false);
      }
    },
    [tokenConfig, searchImpl, fetchImpl, embedQuery],
  );

  // Trigger a search when the SUBMITTED query changes (not on every
  // keystroke). The form's onSubmit sets submittedQuery.
  useEffect(() => {
    if (submittedQuery.length > 0) {
      void doSearch(submittedQuery, mode, semantic);
    }
  }, [submittedQuery, mode, semantic, doSearch]);

  // Start Menu Search → seed the input + auto-run the search. The Start
  // Menu opens this window and dispatches START_MENU_SEARCH_EVENT with
  // the typed query. We listen on the document so we catch the event
  // even when the window mounts after the dispatch (StartMenu also
  // re-fires after a short timeout to handle the race).
  useEffect(() => {
    function onSearch(ev: Event) {
      const detail = (ev as CustomEvent<StartMenuSearchDetail>).detail;
      const q = detail?.query?.trim();
      if (!q) return;
      setQuery(q);
      setSubmittedQuery(q);
    }
    document.addEventListener(START_MENU_SEARCH_EVENT, onSearch);
    return () => document.removeEventListener(START_MENU_SEARCH_EVENT, onSearch);
  }, []);

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setSubmittedQuery(query);
  };

  const placeholderLabel = useMemo(() => {
    if (clientSlug === null) {return 'Open a portal to search';}
    return `Search ${portal?.branding.displayName ?? clientSlug}'s corpus`;
  }, [clientSlug, portal?.branding.displayName]);

  // F-AZURE-SEARCH Phase 3 — no portal context. Show the user's real
  // portal memberships, with sensible fallbacks for unauthenticated
  // visitors and brand-new accounts that haven't joined a portal yet.
  if (clientSlug === null) {
    const hasMemberships =
      Array.isArray(myPortals) && myPortals.length > 0;
    return (
      <div
        data-testid={testIds.corpusSearch.root}
        className="p-4 text-sm text-black flex flex-col gap-3"
      >
        <p className="font-semibold">Corpus Search</p>
        <p className="text-black">
          Search runs against a specific firm&rsquo;s document index.
          Open one of your portals to start:
        </p>
        {myPortals === undefined ? (
          <p className="text-gray-700">Loading your portals…</p>
        ) : hasMemberships ? (
          <div className="flex flex-col gap-1.5">
            {myPortals!.map((p) => (
              <a
                key={p.clientSlug}
                href={`/c/${p.clientSlug}/desktop`}
                className="underline text-[#0a3a8a] hover:text-[#1a55b8]"
              >
                /c/{p.clientSlug} — {p.displayName}{' '}
                <span className="text-[10px] uppercase text-gray-600">
                  ({p.role})
                </span>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-gray-700">
            You aren&rsquo;t a member of any portal yet. Once an owner
            invites you, the portal will show up here.
          </p>
        )}
        <p className="text-black/70 text-xs">
          Once inside a portal you can also open Corpus Search from the
          desktop icon or the Start Menu &gt; Search input.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid={testIds.corpusSearch.root}
      className="flex flex-col h-full text-black"
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-2 border-b border-black/40 bg-black/[0.04] p-2"
      >
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholderLabel}
            data-testid={testIds.corpusSearch.queryInput}
            className="flex-1 border border-black/60 bg-white px-2 py-1 text-sm"
          />
          <button
            type="submit"
            disabled={!tokenConfig || tokenConfig.configured !== true || loading}
            data-testid={testIds.corpusSearch.submit}
            className="border border-black bg-white px-3 py-1 text-sm hover:bg-black/[0.06] disabled:opacity-50"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
          <RoleIndicator />
        </div>

        {/* F-AZURE-SEARCH Phase 3 — plain-language mode toggle with
            per-option tooltips. Default is "Both" (hybrid). */}
        <div
          data-testid={testIds.corpusSearch.modeToggle}
          className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
        >
          <span className="font-semibold uppercase tracking-wide text-gray-800">
            Mode
          </span>
          {MODES.map((m) => (
            <label
              key={m.id}
              className="inline-flex items-center gap-1 cursor-pointer"
              title={m.tooltip}
            >
              <input
                type="radio"
                name="corpus-search-mode"
                value={m.id}
                checked={mode === m.id}
                onChange={() => setMode(m.id)}
                data-testid={testIds.corpusSearch.modeOption(m.id)}
                aria-label={m.label}
              />
              <span aria-hidden="true">{m.icon}</span>
              <span>{m.label}</span>
            </label>
          ))}
          <label
            className="inline-flex items-center gap-1 cursor-pointer ml-2"
            title="Re-reads the top matches with an AI model to put the most relevant ones first. Takes a fraction of a second longer per search. Best for important research; turn off for quick lookups."
          >
            <input
              type="checkbox"
              checked={semantic}
              onChange={(e) => setSemantic(e.target.checked)}
              data-testid={testIds.corpusSearch.semanticToggle}
            />
            <span>Re-rank with AI (more accurate, a bit slower)</span>
          </label>
        </div>

        {/* F-AZURE-SEARCH Phase 3 — inline help disclosure. Collapsed
            by default; mirrors the topic-doc copy. */}
        <details
          className="text-xs text-gray-800"
          open={helpOpen}
          onToggle={(e) => setHelpOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer select-none font-semibold">
            ⓘ How does this work?
          </summary>
          <div className="mt-1 leading-snug">
            <p>
              Repancy reads every document and converts it into a kind
              of mathematical fingerprint that captures what the
              document is <em>about</em> — not just which words it
              contains. Your search query gets the same treatment, and
              we find documents whose fingerprints match yours.
            </p>
            <p className="mt-1">
              This means you can search the way you&rsquo;d describe a
              doc to a colleague —{' '}
              <em>&ldquo;the closing memo for the Acme deal&rdquo;</em>{' '}
              — rather than having to remember the exact filename.
              Putting words in <strong>&ldquo;quotes&rdquo;</strong>{' '}
              still does exact-phrase matching when you need precision.
            </p>
          </div>
        </details>
      </form>

      <div className="flex-1 overflow-auto p-2">
        {tokenConfig === undefined ? (
          <p className="text-sm text-gray-700">Loading search configuration…</p>
        ) : tokenConfig.configured === false ? (
          <p
            data-testid={testIds.corpusSearch.notConfigured}
            className="text-sm text-gray-800 border border-black/30 bg-yellow-50 p-3"
          >
            Corpus Search is not configured for this deploy yet. Set{' '}
            <code className="font-mono">AZURE_SEARCH_ENDPOINT</code> and{' '}
            <code className="font-mono">AZURE_SEARCH_QUERY_KEY</code> in
            Convex environment variables, then re-deploy. Until then this
            window renders an empty state and Skills that depend on it
            short-circuit cleanly.
          </p>
        ) : error !== null ? (
          <p
            data-testid={testIds.corpusSearch.error}
            className="text-sm text-red-900 border border-red-700 bg-red-50 p-3"
          >
            {error}
          </p>
        ) : hits === null ? (
          <p className="text-sm text-gray-700">
            Type a query and press <strong>Search</strong>. Results are
            scoped to the sites your firm has granted you access to.
          </p>
        ) : hits.length === 0 ? (
          <div
            data-testid={testIds.corpusSearch.emptyState}
            className="text-sm text-gray-700 flex flex-col gap-1"
          >
            <p>
              No matches for &ldquo;{submittedQuery}&rdquo;
              {total !== null ? ` (searched ${total} indexed docs)` : ''}.
            </p>
            <ul className="list-disc ml-5 text-xs">
              {mode === 'keyword' ? (
                <li>
                  Switch to <strong>Meaning</strong> or <strong>Both</strong>{' '}
                  if you&rsquo;re not sure of the exact words
                </li>
              ) : null}
              <li>Try shorter, more central terms</li>
              <li>
                Remove any filters (<code>type:</code>, <code>matter:</code>) one
                at a time
              </li>
            </ul>
          </div>
        ) : (
          <ul
            data-testid={testIds.corpusSearch.resultsList}
            className="divide-y divide-black/15"
          >
            {hits.map((hit) => (
              <li
                key={hit.key}
                data-testid={testIds.corpusSearch.resultRow(hit.key)}
                className="py-2"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="text-sm font-semibold">{hit.title}</h4>
                  <span
                    data-testid={testIds.corpusSearch.sourcePill('live')}
                    className="rounded border border-black/40 bg-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-700"
                  >
                    {hit.clientSlug}
                  </span>
                </div>
                {hit.snippet.length > 0 ? (
                  <p
                    className="mt-1 text-xs text-gray-800"
                    dangerouslySetInnerHTML={{
                      __html: hit.snippet
                        .replace(/«/g, '<mark>')
                        .replace(/»/g, '</mark>')
                        .replace(/<em>/g, '<mark>')
                        .replace(/<\/em>/g, '</mark>'),
                    }}
                  />
                ) : null}
                <p className="mt-1 text-[11px] text-gray-600 flex items-center gap-2 flex-wrap">
                  {/* F-AZURE-SEARCH Phase 3 — friendly match badge in
                      Meaning/Both modes. BM25 scores aren't directly
                      interpretable as % so we hide the badge in Keyword
                      mode. */}
                  {(mode === 'vector' || mode === 'hybrid') && (
                    <span
                      title="Based on how similar the document's meaning is to your query."
                      className="inline-flex items-center gap-1 border border-gray-400 bg-white px-1"
                    >
                      <span className="text-gray-700">match</span>
                      <span className="font-semibold">
                        {Math.max(
                          0,
                          Math.min(100, Math.round(hit.score * 100)),
                        )}
                        %
                      </span>
                    </span>
                  )}
                  <span>
                    mode {mode}
                    {semantic ? ' + AI rerank' : ''}
                  </span>
                  {hit.lastModifiedDateTime.length > 0 ? (
                    <span>
                      ·{' '}
                      {new Date(hit.lastModifiedDateTime).toLocaleString()}
                    </span>
                  ) : null}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
